/**
 * Every enum the server is allowed to see.
 *
 * The invariant: the server stores *outcomes*, never *content*. Nothing here can
 * be inverted into a hint, a location, a storage medium, or a passphrase.
 */

/** Which of the two independent drill tracks a drill belongs to. */
export const DRILL_TRACKS = ['A', 'B'] as const;
export type DrillTrack = (typeof DRILL_TRACKS)[number];

/** Track A = recall rehearsal. Track B = physical media inspection. */
export const TRACK_NAMES: Record<DrillTrack, string> = {
  A: 'recall',
  B: 'physical',
};

/** How a drill ended. `failed` means the user could not complete the recall. */
export const DRILL_OUTCOMES = ['success', 'failed', 'abandoned'] as const;
export type DrillOutcome = (typeof DRILL_OUTCOMES)[number];

/** Deepest hint level revealed during the drill. 0 = unaided. */
export const HINT_DEPTHS = [0, 1, 2, 3, 4, 5] as const;
export type HintDepth = (typeof HINT_DEPTHS)[number];
export const MAX_HINT_DEPTH = 5;

/**
 * How the physical backup was verified.
 * - `code`      the user read the recovery code off the paper and it matched
 * - `self`      the user says they held it, but no code was checked
 * - `memory`    the user only recalled it; nothing was physically touched
 * - `none`      no verification at all
 */
export const PHYSICAL_VERIFICATION_TYPES = ['code', 'self', 'memory', 'none'] as const;
export type PhysicalVerificationType = (typeof PHYSICAL_VERIFICATION_TYPES)[number];

/** Coarse "how long did it take to find" bucket. Deliberately two-valued. */
export const FIND_TIME_BUCKETS = ['under_5', 'over_5'] as const;
export type FindTimeBucket = (typeof FIND_TIME_BUCKETS)[number];

/** Physical state of the backup medium, as reported by the user. */
export const BACKUP_CONDITIONS = ['intact', 'minor_wear', 'degraded', 'compromised'] as const;
export type BackupCondition = (typeof BACKUP_CONDITIONS)[number];

/** Anchor lifecycle. `action_required` is set by repeated snoozes or a failure. */
export const ANCHOR_STATUSES = ['active', 'paused', 'action_required', 'archived'] as const;
export type AnchorStatus = (typeof ANCHOR_STATUSES)[number];

/** Drill lifecycle. */
export const DRILL_STATUSES = ['scheduled', 'started', 'completed', 'expired'] as const;
export type DrillStatus = (typeof DRILL_STATUSES)[number];

/** Readiness band derived from the readiness score. */
export const READINESS_BANDS = ['ready', 'due_soon', 'at_risk', 'action_required'] as const;
export type ReadinessBand = (typeof READINESS_BANDS)[number];

/** Preferred local delivery window for reminders. */
export const NOTIFY_WINDOWS = ['morning', 'midday', 'evening'] as const;
export type NotifyWindow = (typeof NOTIFY_WINDOWS)[number];

/** Local hour each window maps to, in the user's own timezone. */
export const NOTIFY_WINDOW_HOUR: Record<NotifyWindow, number> = {
  morning: 9,
  midday: 13,
  evening: 19,
};

/** Whether the on-device vault answered its heartbeat. */
export const LOCAL_VAULT_STATUSES = ['ok', 'lost', 'unknown'] as const;
export type LocalVaultStatus = (typeof LOCAL_VAULT_STATUSES)[number];

export const DEVICE_PLATFORMS = ['ios', 'android', 'web'] as const;
export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];

export const DELIVERY_CHANNELS = ['push', 'email'] as const;
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number];
